import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import ScrollableSection from "../ScrollableSection"
import { ChartPlaceholder } from "~/ui-rtk/components/atoms"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { DateRange } from "../../../types"
import {
  type CollectionTableRow,
  type ProductTableRow,
} from "../../../hooks/useHomeDashboard"
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline"
import useConnectedServices from "~/ui-rtk/hooks/connected-services"
import { CONNECTORS_IDS } from "~/ui-rtk/constants/sources"
import { ConnectionStatus } from "~/ui-rtk/api/types"
import useDemoMode from "~/ui-rtk/hooks/demo-mode"

type MerchandisingTrendsSectionProps = {
  dateRange: DateRange
  topCollections: CollectionTableRow[]
  topProducts: ProductTableRow[]
  isCollectionsLoading?: boolean
  isProductsLoading?: boolean
  hasMerchandisingData?: boolean
  className?: string
}

const PAGE_SIZE = 5

const formatNumber = (value: number) =>
  value.toLocaleString(undefined, { maximumFractionDigits: 0 })

const formatPercent = (value: number) => `${value.toFixed(2)}%`

// Sanitize string values to prevent XSS attacks
const sanitizeString = (value: string): string =>
  value
    .replace(/[<>]/g, "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#x27;")
    .substring(0, 500)

// Pagination Controls
const PaginationControls = ({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}) => {
  if (totalPages <= 1) return null

  return (
    <div className="flex items-center justify-center gap-2 mt-3 pt-3 border-t border-system-blue-700/30">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 0}
        className={cn(
          "p-1 rounded transition-colors",
          currentPage === 0
            ? "text-system-gray-600 cursor-not-allowed"
            : "text-system-gray-400 hover:text-white hover:bg-system-blue-700/50",
        )}
      >
        <ChevronLeftIcon className="w-4 h-4" />
      </button>
      <span className="text-xs text-system-gray-400">
        {currentPage + 1} / {totalPages}
      </span>
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages - 1}
        className={cn(
          "p-1 rounded transition-colors",
          currentPage >= totalPages - 1
            ? "text-system-gray-600 cursor-not-allowed"
            : "text-system-gray-400 hover:text-white hover:bg-system-blue-700/50",
        )}
      >
        <ChevronRightIcon className="w-4 h-4" />
      </button>
    </div>
  )
}

// Collection Funnel Table (matching CollectionFunnelTable style)
const CollectionFunnelTable = ({
  data,
  isLoading,
}: {
  data: CollectionTableRow[]
  isLoading?: boolean
}) => {
  const [page, setPage] = useState(0)
  const { isEnabled: isDemoEnabled } = useDemoMode()
  const totalPages = Math.ceil(data.length / PAGE_SIZE)
  const paginatedData = data.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  if (isLoading) {
    return <ChartPlaceholder title="Loading collections..." icon="📊" />
  }

  if (!data.length) {
    return <ChartPlaceholder title="No collection data available" icon="📊" />
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-system-blue-600/50">
              <th className="text-left py-3 px-3 text-xs font-semibold text-system-gray-400 uppercase tracking-wider">
                Collection
              </th>
              <th className="text-right py-3 px-3 text-xs font-semibold text-system-gray-400 uppercase tracking-wider">
                Views
              </th>
              <th className="text-right py-3 px-3 text-xs font-semibold text-system-gray-400 uppercase tracking-wider">
                Add to Cart
              </th>
              <th className="text-right py-3 px-3 text-xs font-semibold text-system-gray-400 uppercase tracking-wider">
                Purchases
              </th>
              <th className="text-right py-3 px-3 text-xs font-semibold text-system-gray-400 uppercase tracking-wider">
                Conversion
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, idx) => {
              const hasValidTitle =
                row.collectionTitle &&
                row.collectionTitle !== "Unnamed Collection"
              return (
                <tr
                  key={row.collectionId || idx}
                  className={cn(
                    "border-b border-system-blue-700/30 transition-colors hover:bg-system-blue-700/20",
                    idx % 2 === 0 && "bg-system-blue-800/20",
                  )}
                >
                  <td
                    className={cn(
                      "py-3 px-3 text-sm max-w-[180px]",
                      isDemoEnabled && "blur-md",
                    )}
                  >
                    <div className="font-medium text-white truncate">
                      {hasValidTitle
                        ? sanitizeString(row.collectionTitle)
                        : sanitizeString(row.collectionHandle)}
                    </div>
                    {hasValidTitle && (
                      <div className="text-xs text-white/50 truncate">
                        {sanitizeString(row.collectionHandle)}
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-3 text-sm text-white text-right font-medium">
                    {formatNumber(row.collectionViewers)}
                  </td>
                  <td className="py-3 px-3 text-sm text-right">
                    <div className="text-white font-medium">
                      {formatNumber(row.addersToCart)}
                    </div>
                    <div className="text-green-400 text-xs">
                      {formatPercent(row.collectionToCartConversion)}
                    </div>
                  </td>
                  <td className="py-3 px-3 text-sm text-right">
                    <div className="text-white font-medium">
                      {formatNumber(row.purchasers)}
                    </div>
                    <div className="text-green-400 text-xs">
                      {formatPercent(row.cartToPurchaseConversion)}
                    </div>
                  </td>
                  <td className="py-3 px-3 text-sm text-right">
                    <div className="text-green-400 font-semibold">
                      {formatPercent(row.collectionToPurchaseConversion)}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <PaginationControls
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </div>
  )
}

// Product Funnel Table (matching ProductPageFunnelTable style)
const ProductFunnelTable = ({
  data,
  isLoading,
}: {
  data: ProductTableRow[]
  isLoading?: boolean
}) => {
  const [page, setPage] = useState(0)
  const { isEnabled: isDemoEnabled } = useDemoMode()
  const totalPages = Math.ceil(data.length / PAGE_SIZE)
  const paginatedData = data.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  if (isLoading) {
    return <ChartPlaceholder title="Loading products..." icon="📊" />
  }

  if (!data.length) {
    return <ChartPlaceholder title="No product data available" icon="📊" />
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-system-blue-600/50">
              <th className="text-left py-3 px-3 text-xs font-semibold text-system-gray-400 uppercase tracking-wider">
                Product
              </th>
              <th className="text-right py-3 px-3 text-xs font-semibold text-system-gray-400 uppercase tracking-wider">
                Views
              </th>
              <th className="text-right py-3 px-3 text-xs font-semibold text-system-gray-400 uppercase tracking-wider">
                Add to Cart
              </th>
              <th className="text-right py-3 px-3 text-xs font-semibold text-system-gray-400 uppercase tracking-wider">
                Checkouts
              </th>
              <th className="text-right py-3 px-3 text-xs font-semibold text-system-gray-400 uppercase tracking-wider">
                Purchases
              </th>
              <th className="text-right py-3 px-3 text-xs font-semibold text-system-gray-400 uppercase tracking-wider">
                Conversion
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, idx) => {
              const hasValidTitle =
                row.productTitle && row.productTitle !== "Unnamed Product"
              return (
                <tr
                  key={row.productId || idx}
                  className={cn(
                    "border-b border-system-blue-700/30 transition-colors hover:bg-system-blue-700/20",
                    idx % 2 === 0 && "bg-system-blue-800/20",
                  )}
                >
                  <td
                    className={cn(
                      "py-3 px-3 text-sm max-w-[180px]",
                      isDemoEnabled && "blur-md",
                    )}
                  >
                    <div className="font-medium text-white truncate">
                      {hasValidTitle
                        ? sanitizeString(row.productTitle)
                        : sanitizeString(row.productHandle)}
                    </div>
                    {hasValidTitle && (
                      <div className="text-xs text-white/50 truncate">
                        {sanitizeString(row.productHandle)}
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-3 text-sm text-white text-right font-medium">
                    {formatNumber(row.productViewers)}
                  </td>
                  <td className="py-3 px-3 text-sm text-right">
                    <div className="text-white font-medium">
                      {formatNumber(row.addersToCart)}
                    </div>
                    <div className="text-green-400 text-xs">
                      {formatPercent(row.productToCartConversion)}
                    </div>
                  </td>
                  <td className="py-3 px-3 text-sm text-right">
                    <div className="text-white font-medium">
                      {formatNumber(row.checkouts)}
                    </div>
                    <div className="text-green-400 text-xs">
                      {formatPercent(row.cartToCheckoutConversion)}
                    </div>
                  </td>
                  <td className="py-3 px-3 text-sm text-right">
                    <div className="text-white font-medium">
                      {formatNumber(row.purchasers)}
                    </div>
                    <div className="text-green-400 text-xs">
                      {formatPercent(row.checkoutToPurchaseConversion)}
                    </div>
                  </td>
                  <td className="py-3 px-3 text-sm text-right">
                    <div className="text-green-400 font-semibold">
                      {formatPercent(row.productToPurchaseConversion)}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <PaginationControls
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </div>
  )
}

export default function MerchandisingTrendsSection({
  dateRange: _dateRange,
  topCollections,
  topProducts,
  isCollectionsLoading = false,
  isProductsLoading = false,
  hasMerchandisingData = true,
  className,
}: MerchandisingTrendsSectionProps) {
  const navigate = useNavigate()
  const { getCompanyConnectorByService } = useConnectedServices()

  // Check if GA4 custom connector (not Airbyte) is connected
  const ga4CustomConnector = getCompanyConnectorByService(CONNECTORS_IDS.GA4)
  const isGA4CustomConnected =
    ga4CustomConnector?.status === ConnectionStatus.CONNECTED &&
    ga4CustomConnector?.isCompleted

  // Suppress unused variable warning - dateRange is passed for consistency
  void _dateRange

  const tableItems = useMemo(
    () => [
      {
        id: "topCollections",
        title: "Top 10 Collections",
        content: (
          <CollectionFunnelTable
            data={topCollections}
            isLoading={isCollectionsLoading}
          />
        ),
      },
      {
        id: "topProducts",
        title: "Top 10 Products",
        content: (
          <ProductFunnelTable
            data={topProducts}
            isLoading={isProductsLoading}
          />
        ),
      },
    ],
    [topCollections, topProducts, isCollectionsLoading, isProductsLoading],
  )

  // Only check when not loading
  if (!isCollectionsLoading && !isProductsLoading) {
    // If GA4 custom connector is not connected, show connect message
    if (!isGA4CustomConnected) {
      return (
        <div
          className={cn(
            "bg-gradient-to-br from-system-blue-800/80 to-system-blue-900/90 rounded-2xl p-6 border border-system-blue-700/50",
            className,
          )}
          style={{ minHeight: "400px" }}
        >
          <h2 className="text-lg font-semibold text-white mb-4">
            Merchandising Trends
          </h2>
          <div className="flex-1 flex flex-col items-center justify-center h-[320px] text-center space-y-4">
            <div className="text-system-gray-400 text-sm">
              Connect Google Analytics Custom Reports to view merchandising data
            </div>
            <button
              className="px-6 py-2 bg-system-brand-pink-600 hover:bg-system-brand-pink-700 text-white rounded-lg font-medium transition-colors duration-200"
              onClick={() => navigate("/setup/connectors")}
            >
              Connect Google Analytics Custom Reports
            </button>
          </div>
        </div>
      )
    }

    // If GA4 custom connector is connected but no data, show error message
    if (isGA4CustomConnected && !hasMerchandisingData) {
      return (
        <div
          className={cn(
            "bg-gradient-to-br from-system-blue-800/80 to-system-blue-900/90 rounded-2xl p-6 border border-system-blue-700/50",
            className,
          )}
          style={{ minHeight: "400px" }}
        >
          <h2 className="text-lg font-semibold text-white mb-4">
            Merchandising Trends
          </h2>
          <div className="flex-1 flex flex-col items-center justify-center h-[320px] text-center space-y-4">
            <div className="text-red-400 text-sm font-medium">
              You don't have the data
            </div>
            <div className="text-system-gray-400 text-xs">
              No merchandising data is available for the selected date range
            </div>
          </div>
        </div>
      )
    }
  }

  return (
    <ScrollableSection
      title="Merchandising Trends"
      items={tableItems}
      className={className}
    />
  )
}
